import { useEffect, useRef, type CSSProperties } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

export interface RoundTabItem<T extends string> {
  id: T;
  label: string;
  icon: LucideIcon;
}

interface RoundTabsProps<T extends string> {
  items: readonly RoundTabItem<T>[];
  active: T;
  onChange: (id: T) => void;
  /** Cor do círculo ativo (ex.: `passbook.backgroundColor`, a mesma do header). */
  accentColor?: string;
  /** Texto/ícone sobre `accentColor` — o par que a loja escolheu (senão some em cor clara). */
  foregroundColor?: string;
  /** Rótulo para leitores de tela. */
  ariaLabel?: string;
}

/**
 * Bolinhas do topo das páginas públicas (convite e área do participante):
 * círculo com ícone e rótulo embaixo. A ativa fica na cor da loja com halo
 * (`.round-tab-active`); as outras, neutras, para a ativa se destacar.
 *
 * - até 3: espaçamento largo do convite;
 * - 4 e 5: encolhem para caber em 375px;
 * - 6+: fileira com rolagem lateral no celular (snap + bordas esmaecidas, a
 *   ativa vai para o centro); no computador cabem todas, centralizadas.
 */
export function RoundTabs<T extends string>({
  items,
  active,
  onChange,
  accentColor,
  foregroundColor,
  ariaLabel,
}: RoundTabsProps<T>) {
  const scrollable = items.length > 5;
  const compact = items.length > 3 && !scrollable;
  const scrollerRef = useRef<HTMLDivElement>(null);

  // Rolagem lateral: mantém a bolinha ativa visível (centro), sem mexer na rolagem da página.
  useEffect(() => {
    if (!scrollable) return;
    const scroller = scrollerRef.current;
    const button = scroller?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!scroller || !button || scroller.scrollWidth <= scroller.clientWidth) return;
    const left = button.offsetLeft - (scroller.clientWidth - button.offsetWidth) / 2;
    scroller.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [active, scrollable]);

  const accentVars = {
    ...(accentColor ? { '--tab-accent': accentColor } : {}),
    // Cor da loja sem par definido: branco (as cores de marca são quase sempre escuras).
    ...(foregroundColor ? { '--tab-on-accent': foregroundColor } : accentColor ? { '--tab-on-accent': '#fff' } : {}),
  } as CSSProperties;

  const buttons = items.map(({ id, label, icon: Icon }) => {
    const isActive = active === id;
    return (
      <button
        key={id}
        type="button"
        onClick={() => onChange(id)}
        aria-pressed={isActive}
        className={cn(
          'group flex flex-col items-center gap-2 outline-none',
          compact && 'min-w-0 max-w-20 flex-1',
          scrollable && 'w-16 shrink-0 snap-center sm:w-[4.5rem]',
        )}
      >
        <span
          className={cn(
            'grid place-items-center rounded-full transition-[transform,background-color,color,box-shadow,border-color] duration-200 ease-out',
            'group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-background',
            compact ? 'size-12 sm:size-14' : 'size-14',
            isActive
              ? 'round-tab-active scale-105'
              : 'border border-border bg-card text-muted-foreground shadow-soft group-hover:border-foreground/30 group-hover:text-foreground group-active:scale-95',
          )}
        >
          <Icon className="size-5" strokeWidth={isActive ? 2.25 : 2} />
        </span>
        <span
          className={cn(
            'transition-colors',
            compact || scrollable ? 'text-center text-[11px] leading-tight sm:text-xs' : 'text-xs',
            isActive ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground',
          )}
        >
          {label}
        </span>
      </button>
    );
  });

  if (scrollable) {
    return (
      <div
        ref={scrollerRef}
        role="group"
        aria-label={ariaLabel}
        style={accentVars}
        className={cn(
          'relative -mx-4 snap-x snap-proximity overflow-x-auto overscroll-x-contain sm:mx-0',
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          '[mask-image:linear-gradient(to_right,transparent,#000_20px,#000_calc(100%_-_20px),transparent)] sm:[mask-image:none]',
        )}
      >
        <div className="mx-auto flex w-max items-start gap-2 px-5 pb-6 pt-3 sm:gap-3">{buttons}</div>
      </div>
    );
  }

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      style={accentVars}
      className={cn(
        'flex justify-center pb-6 pt-3',
        compact ? 'items-start gap-1.5 sm:gap-4' : 'items-center gap-10',
      )}
    >
      {buttons}
    </div>
  );
}
